import * as React from "react";
import { Box, CPHrBreak, CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPResourceSecurityHelpModal from "shared/components/app/CPResourceSecurityHelpModal/CPResourceSecurityHelpModal";
import PowerBIConfigHelpModal from "./PowerBIConfigHelpModal";
import CPHelpButton from "shared/components/shared/CPHelpButton/CPHelpButton";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPTextArea from "shared/components/shared/CPTextArea/CPTextArea";
import CPCheckbox from "shared/components/shared/CPCheckbox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import useAppState from "shared/state/useAppState";

import {
  useGetCategoriesForResource,
  useGetRolesForResource,
} from "data/resources/resource";
import CPListBox, {
  CPBoxOption,
} from "shared/components/shared/CPListBox/CPListBox";
import {
  ReportResource,
  useUpsertReportMutation,
  useDeleteReportMutation,
} from "data/resources/report";
import { ResourceCategory, ResourceRole } from "data/resources/resource";
import CPSpacer from "shared/components/shared/CPSpacer/CPSpacer";
import { useConfirm } from "shared/components/shared/CPConfirmModal/useCPConfirmModalContext";

export class UpsertReportRecord implements Omit<ReportResource, "agencies"> {
  id: number = -1;
  title: string = "";
  categories: string[] = [];
  roles: string[] = [];
  summary?: string = "";
  resourceTier: string = "";
  isActive: boolean = true;
  tenantId?: string = "";
  workspaceId?: string = "";
  reportId?: string = "";
  datasetId?: string = "";
  requiresEffectiveIdentity?: boolean = true;
  defaultPageName?: string = "";
  pbiRoles?: string = "";
}

interface ReportDetailsModalProps {
  selectedReport: UpsertReportRecord | null;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
}

const ReportDetailsModal: React.FC<ReportDetailsModalProps> = ({
  selectedReport,
  onClose,
  onSave,
  onDelete,
}) => {
  const { setToastMsg, setIsLoading } = useAppState();
  const confirm = useConfirm();
  const { data: categories, isLoading: categoriesLoading } =
    useGetCategoriesForResource(selectedReport?.id ?? -1);
  const { data: roles, isLoading: rolesLoading } = useGetRolesForResource(
    selectedReport?.id ?? -1,
  );
  const {
    mutate,
    isLoading: isSaving,
    error: saveError,
  } = useUpsertReportMutation();
  const { mutate: deleteReport, isLoading: isDeleting } =
    useDeleteReportMutation();
  const [editReport, setEditReport] = React.useState<UpsertReportRecord | null>(
    {
      ...selectedReport,
    } as UpsertReportRecord,
  );

  const [categoryOptions, setCategoryOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [roleOptions, setRoleOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [showSecurityHelp, setShowSecurityHelp] = React.useState(false);
  const [showPowerBIHelp, setShowPowerBIHelp] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(categoriesLoading || rolesLoading || isSaving);
  }, [categoriesLoading, rolesLoading, isSaving, setIsLoading]);

  React.useEffect(() => {
    if (categories) {
      setCategoryOptions({
        available: categories.available.map((c: ResourceCategory) => ({
          value: c.name,
          label: c.parentName ? `${c.name} (${c.parentName})` : c.name,
          selected: false,
        })),
        selected: categories.assigned.map((c: ResourceCategory) => ({
          value: c.name,
          label: c.parentName ? `${c.name} (${c.parentName})` : c.name,
          selected: false,
        })),
      });
    }
  }, [categories]);

  React.useEffect(() => {
    if (roles) {
      setRoleOptions({
        available: roles.available.map((r: ResourceRole) => ({
          value: r.name,
          label: r.name,
          selected: false,
        })),
        selected: roles.assigned.map((r: ResourceRole) => ({
          value: r.name,
          label: r.name,
          selected: false,
        })),
      });
    }
  }, [roles]);

  // When a child category is selected, move the parent as well.
  const handleCategoryChange = React.useCallback(
    (options: CPBoxOption) => {
      if (!categories) {
        setCategoryOptions(options);
        return;
      }

      const allCategories = [...categories.available, ...categories.assigned];

      const getAncestorIds = (name: string): string[] => {
        const cat = allCategories.find((c) => c.name === name);
        if (!cat?.parentName) return [];
        const parent = allCategories.find((c) => c.name === cat.parentName);
        if (!parent) return [];
        return [parent.name, ...getAncestorIds(parent.name)];
      };

      const selectedIds = new Set(options.selected.map((o) => o.value));
      const ancestorIds = new Set(
        options.selected.flatMap((o) => getAncestorIds(o.value as string)),
      );

      const autoPromoted = options.available
        .filter(
          (o) =>
            ancestorIds.has(o.value as string) &&
            !selectedIds.has(o.value as string),
        )
        .map((o) => ({ ...o, selected: false }));

      const allRequiredIds = new Set(
        Array.from(selectedIds).concat(Array.from(ancestorIds)),
      );

      setCategoryOptions({
        available: options.available.filter(
          (o) => !allRequiredIds.has(o.value as string),
        ),
        selected: [...options.selected, ...autoPromoted],
      });
    },
    [categories],
  );

  const handleSave = () => {
    if (!editReport) return;
    mutate(
      {
        ...editReport,
        categories: categoryOptions.selected.map((c) => c.value as string),
        roles: roleOptions.selected.map((r) => r.value as string),
      },
      {
        onSuccess: () => {
          setToastMsg("Report saved successfully", "success");
          onSave();
        },
        onError: (error: unknown) => {
          if ((error as Record<string, unknown>)["errors"]) {
            setToastMsg(
              "Please check the form for errors and try again",
              "error",
            );
            return;
          }

          setToastMsg("An error occurred during the request.", "error");
        },
      },
    );
  };

  const handleDelete = () => {
    if (!editReport) return;
    confirm((confirmed) => {
      if (confirmed) {
        deleteReport(editReport.id, {
          onSuccess: () => {
            setToastMsg("Report deleted successfully", "success");
            onClose();
            onDelete?.();
          },
          onError: () => {
            setToastMsg("Failed to delete report", "error");
          },
        });
      }
    }, `Are you sure you want to delete "${editReport.title}"?`);
  };

  const isNew = selectedReport?.id === -1;

  return (
    <CPActionsheetModal
      title={isNew ? "Add Report" : "Edit Report"}
      open={selectedReport !== null && !rolesLoading && !categoriesLoading}
      onClose={onClose}
      position="right"
      width={550}
      actionBtns={
        <Box spacing={8}>
          {!isNew && (
            <CPButton
              themeColor="error"
              fillMode="outline"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Delete
            </CPButton>
          )}
          <CPButton
            themeColor="primary"
            onClick={handleSave}
            disabled={isDeleting}
          >
            Save
          </CPButton>
        </Box>
      }
    >
      {selectedReport && (
        <Box spacing={24} flexDirection="column" style={{ paddingBottom: 16 }}>
          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <CPTextBox
                toCapital
                required
                name="title"
                label="Report Title"
                value={editReport?.title}
                error={saveError}
                onChange={(e) => {
                  setEditReport(
                    (prev) =>
                      ({
                        ...prev,
                        title: e.target.value,
                      }) as UpsertReportRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPCheckbox
                name="isActive"
                label="Active"
                checked={editReport?.isActive}
                onChange={(e) => {
                  setEditReport(
                    (prev) =>
                      ({
                        ...prev,
                        isActive: e.value,
                      }) as UpsertReportRecord,
                  );
                }}
              />
              <CPText>Show or hide this report</CPText>
            </Box>

            <Box className="cp-actionsheet-input-box">
              <CPTextArea
                name="summary"
                label="Report Summary"
                error={saveError}
                disableResize
                value={editReport?.summary}
                rows={4}
                onChange={(e) => {
                  setEditReport(
                    (prev) =>
                      ({
                        ...prev,
                        summary: e.value,
                      }) as UpsertReportRecord,
                  );
                }}
              />
            </Box>
          </Box>

          <Box
            spacing={12}
            flexDirection="column"
            className="cp-actionsheet-input-box"
          >
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-start"
              spacing={10}
            >
              <Box flexDirection="column" spacing={4}>
                <CPText
                  textStyle="h5"
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <i
                    className="fa-solid fa-chart-line"
                    style={{ marginRight: 8 }}
                  />
                  Power BI Configuration
                </CPText>
                <CPText style={{ opacity: 0.7 }}>
                  Connect to Power BI workspace and report
                </CPText>
              </Box>
              <CPHelpButton
                onClick={() => setShowPowerBIHelp(true)}
                title="Power BI configuration help"
                aria-label="Power BI configuration help"
              />
            </Box>
            <CPTextBox
              name="workspaceId"
              label="Workspace ID"
              required
              value={editReport?.workspaceId}
              onChange={(e) => {
                setEditReport(
                  (prev) =>
                    ({
                      ...prev,
                      workspaceId: e.target.value,
                    }) as UpsertReportRecord,
                );
              }}
            />
            <CPTextBox
              name="reportId"
              label="Report ID"
              required
              value={editReport?.reportId}
              onChange={(e) => {
                setEditReport(
                  (prev) =>
                    ({
                      ...prev,
                      reportId: e.target.value,
                    }) as UpsertReportRecord,
                );
              }}
            />
            <CPTextBox
              name="tenantId"
              label="Tenant ID"
              value={editReport?.tenantId}
              onChange={(e) => {
                setEditReport(
                  (prev) =>
                    ({
                      ...prev,
                      tenantId: e.target.value,
                    }) as UpsertReportRecord,
                );
              }}
            />

            <CPTextBox
              name="datasetId"
              label="Dataset ID"
              value={editReport?.datasetId}
              onChange={(e) => {
                setEditReport(
                  (prev) =>
                    ({
                      ...prev,
                      datasetId: e.target.value,
                    }) as UpsertReportRecord,
                );
              }}
            />

            <CPTextBox
              name="defaultPageName"
              label="Default Page Name"
              value={editReport?.defaultPageName}
              onChange={(e) => {
                setEditReport(
                  (prev) =>
                    ({
                      ...prev,
                      defaultPageName: e.target.value,
                    }) as UpsertReportRecord,
                );
              }}
            />

            <CPCheckbox
              name="requiresEffectiveIdentity"
              label="Requires Effective Identity (RLS)"
              checked={editReport?.requiresEffectiveIdentity ?? true}
              onChange={(e) => {
                setEditReport(
                  (prev) =>
                    ({
                      ...prev,
                      requiresEffectiveIdentity: e.value,
                      pbiRoles: e.value
                        ? prev?.pbiRoles || "Agent Security"
                        : "",
                    }) as UpsertReportRecord,
                );
              }}
            />
            {editReport?.requiresEffectiveIdentity && (
              <CPTextBox
                name="pbiRoles"
                label="RLS Role"
                value={editReport?.pbiRoles}
                onChange={(e) => {
                  setEditReport(
                    (prev) =>
                      ({
                        ...prev,
                        pbiRoles: e.target.value,
                      }) as UpsertReportRecord,
                  );
                }}
              />
            )}
          </Box>

          <Box spacing={12} flexDirection="column" style={{ padding: 0 }}>
            <Box className="cp-actionsheet-input-box">
              <Box
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                spacing={10}
              >
                <Box flexDirection="column" spacing={4}>
                  <CPText
                    textStyle="h6"
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i
                      className="fa-solid fa-lock"
                      style={{ marginRight: 8 }}
                    />
                    Access & Security
                  </CPText>
                  <CPText style={{ opacity: 0.7 }}>
                    Control who can access this resource
                  </CPText>
                </Box>
                <CPHelpButton
                  onClick={() => setShowSecurityHelp(true)}
                  title="How does security work?"
                  aria-label="Visibility help"
                />
              </Box>
              <CPSpacer height={15} />
              <Box>
                <CPText textStyle="subtitle" style={{ fontSize: 13 }}>
                  Resource Tier
                </CPText>
                <CPText
                  textStyle="caption"
                  style={{ opacity: 0.8, fontSize: 12, marginTop: 4 }}
                >
                  Lock the resource to agencies with matching tier access.
                </CPText>
              </Box>

              <CPSpacer height={8} />

              <CPDropDownList
                name="resourceTier"
                label="Tier"
                textField="label"
                dataItemKey="value"
                valueKey="value"
                defaultItem={{ label: "Please choose...", value: null }}
                data={[
                  { label: "Bronze", value: "BRONZE" },
                  { label: "Silver", value: "SILVER" },
                  { label: "Gold", value: "GOLD" },
                ]}
                value={editReport?.resourceTier ?? null}
                onChange={(e) => {
                  setEditReport(
                    (prev) =>
                      ({
                        ...prev,
                        resourceTier: e.value,
                      }) as UpsertReportRecord,
                  );
                }}
              />
            </Box>

            <Box className="cp-actionsheet-input-box">
              <CPListBox
                name="categories"
                headerText="Assigned Categories *"
                filterable
                boxOptions={categoryOptions}
                onChange={handleCategoryChange}
                error={saveError}
              />
            </Box>

            <Box className="cp-actionsheet-input-box">
              <CPListBox
                name="roles"
                headerText="Assigned Roles"
                filterable
                boxOptions={roleOptions}
                onChange={(options) => setRoleOptions(options)}
              />
            </Box>
          </Box>
        </Box>
      )}

      <CPResourceSecurityHelpModal
        open={showSecurityHelp}
        onClose={() => setShowSecurityHelp(false)}
      />
      <PowerBIConfigHelpModal
        open={showPowerBIHelp}
        onClose={() => setShowPowerBIHelp(false)}
      />
    </CPActionsheetModal>
  );
};

export default ReportDetailsModal;
